// Shapes of crm-billing's affiliate operations (internal/affiliates/affiliates.go).
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";

export interface Affiliate {
  id: number;
  keap_id: number | null;
  contact_id: number;
  code: string;
  status: string;
  parent_affiliate_id: number | null;
  default_program_id: number | null;
  payout_method: string | null;
  payout_email: string | null;
  notes: string | null;
  /** Check mailing address the partner gave in the partner center (no bank or card numbers). */
  payout_address: PayoutAddress | null;
  /** When the partner last changed their payout details in the partner center. */
  payout_updated_at: string | null;
  created_at: string;
  earned_cents: number;
  unpaid_cents: number;
  payable_cents: number;
  paid_cents: number;
  clicks: number;
}

export interface PayoutAddress {
  name?: string;
  line1?: string;
  line2?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country?: string;
}

/** A check mailing address as printable lines ("" parts dropped). */
export function addressLines(a: PayoutAddress | null | undefined): string[] {
  if (!a) return [];
  const cityLine = [a.city, [a.region, a.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [a.name, a.line1, a.line2, cityLine, a.country].map((x) => (x ?? "").trim()).filter(Boolean);
}

export interface Program {
  id: number;
  keap_id: number | null;
  name: string;
  description: string | null;
  level1_rate_bps: number;
  level2_rate_bps: number;
  cookie_days: number;
  active: boolean;
}

export interface RateOverride {
  id: number;
  program_id: number;
  product_id: number;
  affiliate_id: number | null;
  level: number;
  rate_bps: number | null;
  flat_cents: number | null;
  starts_at: string | null;
  ends_at: string | null;
  note: string | null;
}

export interface Commission {
  id: number;
  keap_id: number | null;
  affiliate_id: number;
  order_id: number | null;
  payment_id: number | null;
  refund_id: number | null;
  level: number;
  rate_bps: number | null;
  base_cents: number;
  amount_cents: number;
  status: string;
  reversal_of_id: number | null;
  payout_id: number | null;
  earned_at: string;
}

export interface Payout {
  id: number;
  keap_id: number | null;
  affiliate_id: number;
  amount_cents: number;
  method: string | null;
  reference: string | null;
  note: string | null;
  imported: boolean;
  paid_at: string;
  created_by: string;
}

export interface AffiliateLink {
  id: number;
  affiliate_id: number | null;
  brand_id: number | null;
  slug: string;
  name: string | null;
  destination_url: string;
  active: boolean;
  created_at: string;
}

type Variant = "success" | "warning" | "destructive" | "muted" | "default";

export const PARTNER_STATUS: Record<string, [string, Variant]> = {
  active: ["active", "success"],
  pending: ["waiting for approval", "warning"],
  inactive: ["inactive", "muted"],
  banned: ["banned", "destructive"],
};

export const COMMISSION_STATUS: Record<string, [string, Variant]> = {
  pending: ["unpaid", "warning"],
  approved: ["unpaid", "warning"],
  paid: ["paid", "success"],
  reversed: ["taken back (refund)", "destructive"],
  void: ["void", "muted"],
};

export const PAYOUT_METHODS: [string, string][] = [
  ["check", "Check"],
  ["paypal", "PayPal"],
  ["ach", "Bank transfer (ACH)"],
  ["other", "Other"],
];

export const methodLabel = (m: string | null | undefined) => (m ? (PAYOUT_METHODS.find(([k]) => k === m)?.[1] ?? m) : "—");

/** Commissions wait this many days for refunds before they can be paid out (affiliates.HoldDays). */
export const HOLD_DAYS = 30;

/** 5000 basis points → "50%"; 1250 → "12.5%". */
export function pct(bps: number | null | undefined): string {
  if (bps == null) return "—";
  const v = bps / 100;
  return `${Number.isInteger(v) ? v : v.toFixed(2).replace(/0$/, "")}%`;
}

/** The day an unpaid commission becomes payable (earned + 30 days), as an ISO instant. */
export function payableFrom(earnedAt: string): string {
  return new Date(new Date(earnedAt).getTime() + HOLD_DAYS * 86_400_000).toISOString();
}

/** Every partner (the list pages by 200), for code and name look-ups in commission and payout lists. */
export function useAllAffiliates() {
  return useQuery({
    queryKey: ["affiliates", "all"],
    queryFn: async () => {
      const all: Affiliate[] = [];
      let before: number | undefined;
      for (let i = 0; i < 20; i++) {
        const r = await api<{ affiliates: Affiliate[]; next_before_id?: number }>("/api/v1/affiliates" + qs({ limit: 200, before_id: before }));
        all.push(...r.affiliates);
        if (!r.next_before_id) break;
        before = r.next_before_id;
      }
      return all;
    },
    staleTime: 30_000,
  });
}

export function usePrograms() {
  return useQuery({ queryKey: ["affiliate-programs"], queryFn: () => api<{ programs: Program[] }>("/api/v1/affiliate-programs"), staleTime: 60_000 });
}
